import _defineProperty from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
import _objectWithoutProperties from "/opt/build/repo/node_modules/@babel/runtime/helpers/esm/objectWithoutProperties.js";
var _excluded = ["as", "children", "className", "href", "disabled", "inline", "visited", "renderIcon", "size", "target"];
/**
 * Copyright IBM Corp. 2016, 2023
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import cx from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { usePrefix } from '../../internal/usePrefix.js';

// First create the component with basic types
var LinkBase = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var _cx;
  var BaseComponent = _ref.as,
    children = _ref.children,
    customClassName = _ref.className,
    href = _ref.href,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$inline = _ref.inline,
    inline = _ref$inline === void 0 ? false : _ref$inline,
    _ref$visited = _ref.visited,
    visited = _ref$visited === void 0 ? false : _ref$visited,
    Icon = _ref.renderIcon,
    size = _ref.size,
    target = _ref.target,
    rest = _objectWithoutProperties(_ref, _excluded);
  var prefix = usePrefix();
  var className = cx("".concat(prefix, "--link"), customClassName, (_cx = {}, _defineProperty(_cx, "".concat(prefix, "--link--disabled"), disabled), _defineProperty(_cx, "".concat(prefix, "--link--inline"), inline), _defineProperty(_cx, "".concat(prefix, "--link--visited"), visited), _defineProperty(_cx, "".concat(prefix, "--link--").concat(size), size), _cx));
  var rel = target === '_blank' ? 'noopener' : undefined;
  var linkProps = {
    className: className,
    rel: rel,
    target: target
  };

  // Reference for disabled links:
  // https://www.scottohara.me/blog/2021/05/28/disabled-links.html
  if (!disabled) {
    linkProps.href = href;
  } else {
    linkProps.role = 'link';
    linkProps['aria-disabled'] = true;
  }
  var BaseComponentAsAny = BaseComponent !== null && BaseComponent !== void 0 ? BaseComponent : 'a';
  return /*#__PURE__*/React.createElement(BaseComponentAsAny, _extends({
    ref: ref
  }, linkProps, rest), children, !inline && Icon && /*#__PURE__*/React.createElement("div", {
    className: "".concat(prefix, "--link__icon")
  }, /*#__PURE__*/React.createElement(Icon, null)));
});
var Link = LinkBase;
Link.displayName = 'Link';
Link.propTypes = {
  /**
   * Provide a custom element or component to render the top-level node for the
   * component.
   */
  as: PropTypes.elementType,
  /**
   * Provide the content for the Link
   */
  children: PropTypes.node,
  /**
   * Provide a custom className to be applied to the containing `<a>` node
   */
  className: PropTypes.string,
  /**
   * Specify if the control should be disabled, or not
   */
  disabled: PropTypes.bool,
  /**
   * Provide the `href` attribute for the `<a>` node
   */
  href: PropTypes.string,
  /**
   * Specify whether you want the inline version of this control
   */
  inline: PropTypes.bool,
  /**
   * A component used to render an icon.
   */
  renderIcon: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  /**
   * Specify the size of the Link. Currently supports either `sm`, 'md' (default) or 'lg` as an option.
   */
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  /**
   * Specify whether you want the link to receive visited styles after the link has been clicked
   */
  visited: PropTypes.bool
};
export { Link as default };